import React, { useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, IconButton, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

function StatusCell({ expired }) {
  return expired
    ? <Typography sx={{ color: "#CC3A2E", fontWeight: 700, fontSize: 13 }}>Expired</Typography>
    : <Typography sx={{ color: "#12885A", fontWeight: 700, fontSize: 13 }}>Valid</Typography>;
}

export default function OtpLookup() {
  const history = useHistory();
  const [nid,      setNid]      = useState("");
  const [phone,    setPhone]    = useState("");
  const [email,    setEmail]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [results,  setResults]  = useState(null);
  const [error,    setError]    = useState("");

  const search = () => {
    if (!nid && !phone && !email) {
      setError("Enter a National ID, phone number, or email to search.");
      return;
    }
    setError("");
    setResults(null);
    setLoading(true);
    axios
      .get("/api/support-desk/otp-lookup", { params: { nid, phone, email } })
      .then(({ data }) => setResults(data))
      .catch((e) => setError(e?.response?.data || "Failed to look up codes."))
      .finally(() => setLoading(false));
  };

  const totalFound = (results?.supportDesk?.length || 0) + (results?.trainingProvider?.length || 0);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 960 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <IconButton size="small" onClick={() => history.push("/in/support-desk")}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <VpnKeyIcon sx={{ color: "#F16522" }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>OTP Lookup</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Use this when someone says they never received a verification code by SMS or email — whether
        that's a teacher on the Support Desk, or someone registering at{" "}
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>/training-provider</Typography>.
        Sending a code is fire-and-forget — a result here means a code was generated and a send was
        attempted, not that delivery was confirmed. If a valid, unexpired code exists, you can read it
        out directly instead of chasing the gateway.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{typeof error === "string" ? error : JSON.stringify(error)}</Alert>}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3, alignItems: "center" }}>
        <TextField
          size="small" label="National ID"
          value={nid} onChange={(e) => setNid(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          helperText="Support Desk only"
          sx={{ flex: "1 1 200px" }}
        />
        <TextField
          size="small" label="Phone number"
          value={phone} onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          sx={{ flex: "1 1 200px" }}
        />
        <TextField
          size="small" label="Email address"
          value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          sx={{ flex: "1 1 200px" }}
        />
        <Button
          variant="contained" color="warning" onClick={search} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? "Searching…" : "Search"}
        </Button>
      </Box>

      {results && totalFound === 0 && (
        <Alert severity="info">No codes found for this search. No code has been sent yet for this contact.</Alert>
      )}

      {!!results?.supportDesk?.length && (
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Support Desk</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>NID</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Attempts</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.supportDesk.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ fontFamily: "monospace" }}>{r.nid}</TableCell>
                  <TableCell>{r.phone || "—"}</TableCell>
                  <TableCell>{r.email || "—"}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{r.code}</TableCell>
                  <TableCell>{r.attempts}/5</TableCell>
                  <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{new Date(r.expiresAt).toLocaleString()}</TableCell>
                  <TableCell><StatusCell expired={r.expired} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {!!results?.trainingProvider?.length && (
        <Box>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Training Provider Registration</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone OTP</TableCell>
                <TableCell>Email OTP</TableCell>
                <TableCell>Sent (approx.)</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.trainingProvider.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.phone || "—"}</TableCell>
                  <TableCell>{r.email || "—"}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{r.phoneOtp}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{r.emailOtp}</TableCell>
                  <TableCell>{new Date(r.sentAt).toLocaleString()}</TableCell>
                  <TableCell>{new Date(r.expiresAt).toLocaleString()}</TableCell>
                  <TableCell><StatusCell expired={r.expired} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
